// OAuth service for authentication
import { config } from './config';
import { api } from './api';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateRandomState,
  savePKCEParams,
  getPKCEParams,
  clearPKCEParams,
} from './pkce';

export class OAuthService {
  async startLogin(returnTo?: string): Promise<void> {
    // Save return URL for after login
    if (returnTo) {
      sessionStorage.setItem('oauth_return_to', returnTo);
    }

    // Generate PKCE parameters
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateRandomState();

    // Save for later verification
    savePKCEParams(verifier, state);

    // Build OAuth scopes (sandbox now supports email and profile)
    const scopes = ['openid', 'email', 'profile'];

    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.oauth.clientId,
      redirect_uri: config.oauth.redirectUri,
      scope: scopes.join(' '),
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    });

    const authUrl = `${config.oauth.authorizeUrl}?${params.toString()}`;

    // Redirect to Pantaris IAM
    window.location.href = authUrl;
  }

  async handleCallback(): Promise<string | null> {
    try {
      // Check URL params first (direct navigation)
      let urlParams = new URLSearchParams(window.location.search);
      let code = urlParams.get('code');
      let state = urlParams.get('state');
      
      // If not in URL, check sessionStorage (from App.tsx redirect)
      if (!code && sessionStorage.getItem('oauth_callback_code')) {
        code = sessionStorage.getItem('oauth_callback_code');
        state = sessionStorage.getItem('oauth_callback_state');
        
        // Clean up
        sessionStorage.removeItem('oauth_callback_code');
        sessionStorage.removeItem('oauth_callback_state');
      }
      
      const error = urlParams.get('error');

      // Check for OAuth errors
      if (error) {
        console.error('[OAuth] OAuth error:', error);
        return null;
      }

      if (!code || !state) {
        console.error('[OAuth] Missing code or state parameter');
        return null;
      }

      // Get stored PKCE parameters
      const pkceParams = getPKCEParams();
      if (!pkceParams) {
        console.error('[OAuth] Missing PKCE parameters');
        return null;
      }

      // Verify state parameter (CSRF protection)
      if (state !== pkceParams.state) {
        console.error('[OAuth] State mismatch - possible CSRF attack');
        return null;
      }

      // Exchange code for token via backend (creates session)
      await api.completeOAuthCallback(code, pkceParams.verifier, state);

      // Clean up PKCE params
      clearPKCEParams();

      // Get return URL
      const returnTo = sessionStorage.getItem('oauth_return_to') || '/';
      sessionStorage.removeItem('oauth_return_to');
      
      return returnTo;
    } catch (error) {
      console.error('[OAuth] Failed to handle OAuth callback:', error);
      clearPKCEParams();
      return null;
    }
  }

  logout(): void {
    // Clear session cookie by calling backend logout endpoint (TODO: Phase 3)
    clearPKCEParams();
    sessionStorage.removeItem('oauth_return_to');
  }
}

export const oauthService = new OAuthService();
